import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    TableHeader,
    Pagination,
    InfoBanner,
    StarRating,
    Spinner,
    FiltersButton,
    WithRoleAccess,
} from "@services/ui-components";
import { useBranches } from "../../hooks/useBranches";
import { Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import LogoDefault from "../../images/logo-add.png";
import BranchModalDelete from "./BranchModalDelete";
import AccessService from "@me-team/host/services/AccessService";
import BranchConfirmDelete from "./BranchConfirmDelete";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import { useDeleteBranchMutation } from "@me-team/host/main/branches/graphql/branches.hooks";
import { isRefetchBranches } from "@me-team/host/main/branches/state";
import { useReactiveVar } from "@apollo/client";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import {
    currentBranchEmployeeIdVar,
    currentBranchIdVar,
    currentCompanyIdVar,
} from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../hooks/useScrollToTopOnChange";
import { useRoleAccess, useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";

const Branch: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [currentFilter, setCurrentFilter] = useState({ name: "", branchTypeId: "" });
    const [filter, setFilter] = useState({ name: "", branchTypeId: "" });
    const [showFilters, setShowFilters] = useState(false);
    const [showModalDeleteBranch, setShowModalDeleteBranch] = useState<boolean>(false);
    const [showModalConfirmDeleteBranch, setShowModalConfirmDeleteBranch] = useState<boolean>(false);
    const [branchIdToDelete, setBranchIdToDelete] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const toggleFilters = () => setShowFilters(!showFilters);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const accessService = AccessService.getInstance();
    const canCreateBranch = useRoleAccess(CompanyAccess.BranchCreate);
    const canOpenBranchPage = useRoleAccessAny([CompanyAccess.BranchView, CompanyAccess.BranchEdit]);
    const canDeleteRestoreBranch = useRoleAccess(CompanyAccess.BranchDeleteRestore);

    useScrollToTopOnChange(currentPage);

    const [deleteBranch] = useDeleteBranchMutation();

    const basePath = process.env.REACT_APP_POS_URL;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const isDeletedFilter = canDeleteRestoreBranch ? null : false;
    const {
        data: branchesData,
        loading,
        error,
        refetch,
    } = useBranches(
        currentPage,
        itemsPerPage,
        filter,
        currentCompanyId,
        currentBranchEmployeeId,
        false,
        isDeletedFilter
    );

    const handleCloseModalBranch = () => {
        setShowModalDeleteBranch(false);
    };

    const handleOpenConfirmModalBranch = (branchId: number) => {
        if (!canDeleteRestoreBranch) return;

        setBranchIdToDelete(branchId);
        setShowModalConfirmDeleteBranch(true);
    };

    const handleCloseConfirmModalBranch = () => {
        setBranchIdToDelete(null);
        setShowModalConfirmDeleteBranch(false);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentFilter({ ...currentFilter, name: event.target.value });
    };

    // TODO расскоментировать функцию, когда будет расскоментирован селект ниже Type of branch

    // const handleBranchTypeChange = (selectedOption: { value: string; label: string } | null) => {
    //     setCurrentFilter((prevFilter) => ({
    //         ...prevFilter,
    //         branchTypeId: selectedOption ? selectedOption.value : "",
    //     }));
    // };

    const handleApplyFilter = () => {
        setFilter(currentFilter);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setFilter({ name: "", branchTypeId: "" });
        setCurrentFilter({ name: "", branchTypeId: "" });
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleActiveBranchChange = (branchID: number) => {
        currentBranchIdVar(branchID);
        navigate(`/branch/role-management/${branchID}`);
    };

    const handleAccessSettings = (branchID: number) => {
        currentBranchIdVar(branchID);
        navigate(`/branch/access-settings/${branchID}`);
    };

    const handleCreateBranch = () => {
        if (!canCreateBranch || !hasAbilityToCreateBranch) return;
        navigate(`/create-branch/${branchId}`);
    };

    const handleRestoreBranch = (restoreBranchId: number) => {
        if (!canDeleteRestoreBranch || !hasAbilityToCreateBranch) return;
        navigate(`/restore-branch/${restoreBranchId}/${branchId}`);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    // TODO расскоментровать, когда будет необходимо, используется в таблице

    // const colorTypeOfBranch = (type: string) => {
    //     switch (type) {
    //         case "Restaurant":
    //             return "#73D2FB";
    //         case "Cafe":
    //             return "#1AB394";
    //         case "Bar":
    //             return "#DC3545";
    //         case "Eatery":
    //             return "#5842BE";
    //         case "Fast-food":
    //             return "#EA89C2";
    //         case "Pub":
    //             return "#FFD600";
    //         case "Beauty salon":
    //             return "#EA89C2";
    //         default:
    //             return "#DEDEDE";
    //     }
    // };

    const branches = branchesData?.user?.company?.branchesPaginated?.branches || [];

    const hasEmployeeBranchRelation = (branch: (typeof branches)[number]) =>
        Boolean(
            branch.employeesPaginated?.employees?.some((employee) =>
                employee?.userBranchRelations?.some((relation) => relation.branch.id === branch.id)
            )
        );

    const activeBranches = branches?.filter((branch) => !branch.deleted) || [];
    const accessibleActiveBranches = activeBranches.filter(hasEmployeeBranchRelation);
    const pageCount = parseInt(branchesData?.user?.company?.branchesPaginated?.pageCount || "0");
    const totalItems = branchesData?.user?.company?.branchesPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const hasAbilityToCreateBranch = accessService.isPremium || activeBranches.length < 2;

    const handleDeleteBranch = async () => {
        if (!canDeleteRestoreBranch) return;

        if (branchIdToDelete !== null) {
            setIsLoading(true);
            try {
                await deleteBranch({
                    variables: { id: branchIdToDelete },
                    onCompleted: () => {
                        let branchIdToNavigate;
                        branchIdToDelete === Number(branchId)
                            ? (branchIdToNavigate = accessibleActiveBranches.filter(
                                  (ab) => ab.id !== branchIdToDelete
                              )[0]?.id)
                            : (branchIdToNavigate = branchId);
                        navigate(`/branches/${branchIdToNavigate}`);
                    },
                });
                refetch();
                toggleShowToast(t("Deleted"));
                isRefetchBranches(true);
            } catch (error) {
                console.error("Error deleting branch:", error);
                if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.latest") {
                    setShowModalDeleteBranch(true);
                }
                setIsLoading(false);
            } finally {
                handleCloseConfirmModalBranch();
                setIsLoading(false);
            }
        }
    };

    if (loading) return <Spinner />;
    if (error) return <p>Error: {error.message}</p>;

    return (
        <div className="mb-4">
            <InfoBanner positionIcon="top">
                <p className="fs-7 mb-0">
                    {t(
                        "This page displays all branches of your company. You can create new branches, delete and restore existing ones,as well as manage access permissions for each branch."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("branches")}
                paths={[{ path: `/branches/${branchId}`, label: t("branches") }]}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <Col xs={7} md={6} lg={7} xxl={5} className="mb-2 mb-sm-0">
                            <WithRoleAccess permission={CompanyAccess.BranchCreate}>
                                <Button
                                    variant="primary"
                                    className="w-100 fw-bold rounded-1"
                                    disabled={!hasAbilityToCreateBranch}
                                    onClick={handleCreateBranch}
                                >
                                    <i className="bi bi-plus-lg me-2 px-1"></i>
                                    {t("Add Branch")}
                                </Button>
                            </WithRoleAccess>
                        </Col>
                        <Col xs={5} md={6} lg={5} xxl={4}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-none d-sm-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={7}
                btnColSize={5}
            >
                <Row className="gx-4 pb-6">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col>
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        id="brunchList-name-field"
                                        type="text"
                                        className="form-control rounded-1"
                                        placeholder={t("Search by name")}
                                        value={currentFilter.name}
                                        onChange={handleNameChange}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-3"></i>
                                </div>
                            </Col>
                            {/* Temporarily hide the ‘Type of branch’ filter since currently only one type is available. The filter will be shown again when multiple types are introduced. */}
                            {/*<Col md={6} className="mb-3 mb-lg-0">*/}
                            {/*    <ReactSelect*/}
                            {/*        id="brunchList-type-field"*/}
                            {/*        value={selectedBranchType}*/}
                            {/*        options={branchTypeOptions}*/}
                            {/*        placeholder={t("Type of branch")}*/}
                            {/*        onChange={handleBranchTypeChange}*/}
                            {/*        isSearchable={true}*/}
                            {/*        disabled={false}*/}
                            {/*        onKeyDown={handleSearchKeyDown}*/}
                            {/*    />*/}
                            {/*</Col>*/}
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    id="branch-clear-button"
                                    variant="outline-dark"
                                    className="border-grey w-100 h-100 fw-normal rounded-1"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    id="branch-apply-button"
                                    variant="primary"
                                    className="w-100 h-100 rounded-1 fw-normal px-2"
                                    onClick={handleApplyFilter}
                                >
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            {!accessService.isPremium && (
                <InfoBanner>
                    <p className="fs-7 text-dark mb-0">
                        {t(
                            "Only 2 active establishments are available with your tariff. For unlimited, order a tariff"
                        )}
                        <WithRoleAccess permission={CompanyAccess.TariffView} fallback={" Premium"}>
                            <Link
                                to={`/payment/company/${currentCompanyId}/tariff/${branchId}`}
                                className="text-primary ms-1"
                            >
                                Premium
                            </Link>
                        </WithRoleAccess>
                        .
                    </p>
                </InfoBanner>
            )}

            {branches?.length ? (
                <div className="table-responsive scrollbar mb-4">
                    <table className="table mt-6 mb-2">
                        <thead>
                            <tr className="fs-7">
                                <th className="col-1 text-center px-4">{t("logo")}</th>
                                <th className="col-2">{t("name")}</th>
                                <th className="col-4">{t("description")}</th>
                                <th className="col-4">{t("Employees")}</th>
                                {/*<th className="col-2 ps-4">{t("type")}</th>*/}
                                <th className="col-2">{t("rating")}</th>
                                <th className="col-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {branches.map((branch) => {
                                const hasBranchAccess = hasEmployeeBranchRelation(branch);

                                return branch.deleted ? (
                                    <tr key={branch.id} className="text-secondary">
                                        <td className="align-middle">
                                            <div
                                                className="position-relative d-flex justify-content-center align-items-center rounded-circle border border-secondary mx-auto"
                                                style={{ width: "40px", height: "40px" }}
                                            >
                                                <img
                                                    src={branch?.image ? `${basePath}/${branch.image}` : LogoDefault}
                                                    alt={branch?.name}
                                                    className="rounded-circle mw-100 mh-100"
                                                />
                                                <div className="position-absolute top-0 left-0 w-100 h-100 rounded-circle opacity-50 bg-secondary"></div>
                                            </div>
                                        </td>
                                        <td className="text-secondary align-middle">{branch?.name}</td>
                                        <td>
                                            <p className="text-line-clamp text-secondary mb-0">{branch?.description}</p>
                                        </td>
                                        <td>
                                            <p className="text-line-clamp text-secondary mb-0">
                                                {branch?.totalEmployees?.totalCount}
                                            </p>
                                        </td>
                                        {/*<td>*/}
                                        {/*    {branch.type?.name && (*/}
                                        {/*        <div className="d-inline-block p-2 rounded bg-additional-grey text-secondary text-nowrap">*/}
                                        {/*            <span>{branch.type?.name}</span>*/}
                                        {/*        </div>*/}
                                        {/*    )}*/}
                                        {/*</td>*/}
                                        <td>
                                            <div className="d-flex">
                                                <StarRating rating={branch.rating} fontSize={6} textColor="secondary" />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end align-items-center flex-nowrap">
                                                <Button
                                                    variant="additional-grey"
                                                    className="text-grey px-3 py-2 fw-normal rounded-1"
                                                >
                                                    {t("Archive")}
                                                </Button>
                                                <WithRoleAccess permission={CompanyAccess.BranchDeleteRestore}>
                                                    <OverlayTrigger overlay={<Tooltip>{t("Restore")}</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={
                                                                !hasAbilityToCreateBranch || !canDeleteRestoreBranch
                                                            }
                                                            onClick={() => handleRestoreBranch(branch.id)}
                                                            className="text-dark rounded-1"
                                                        >
                                                            <i className="bi bi-arrow-counterclockwise d-flex fs-normal"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </WithRoleAccess>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={branch.id}>
                                        <td className="align-middle">
                                            <div
                                                className="d-flex justify-content-center align-items-center rounded-circle border border-secondary mx-auto"
                                                style={{ width: "40px", height: "40px" }}
                                            >
                                                <img
                                                    src={branch?.image ? `${basePath}/${branch.image}` : LogoDefault}
                                                    alt={branch?.name}
                                                    className="rounded-circle mw-100 mh-100"
                                                />
                                            </div>
                                        </td>
                                        <td className="align-middle">
                                            <WithRoleAccess
                                                permission={CompanyAccess.BranchView}
                                                fallback={<span>{branch?.name}</span>}
                                            >
                                                {hasBranchAccess ? (
                                                    <Link
                                                        to={`/edit-branch/${branch.id}/${branchId}`}
                                                        onClick={() => currentBranchIdVar(branch.id)}
                                                        className="text-primary text-decoration-none"
                                                    >
                                                        {branch?.name}
                                                    </Link>
                                                ) : (
                                                    <span>{branch?.name}</span>
                                                )}
                                            </WithRoleAccess>
                                        </td>
                                        <td>
                                            <p className="text-line-clamp mb-0">{branch?.description}</p>
                                        </td>
                                        <td>
                                            <p className="text-line-clamp mb-0">{branch?.totalEmployees?.totalCount}</p>
                                        </td>
                                        {/*<td>*/}
                                        {/*    <div className="d-inline-block p-2 rounded"*/}
                                        {/*         style={{ backgroundColor: hexToRgbaWithOpacity(colorTypeOfBranch(branch?.type?.name ? branch?.type?.name : branch?.category?.name)), color: colorTypeOfBranch(branch?.type?.name ? branch?.type?.name : branch?.category?.name) }}>*/}
                                        {/*        <span className="text-nowrap">{branch?.type ? branch?.type?.name : t(branch?.category?.name)}</span>*/}
                                        {/*    </div>*/}
                                        {/*</td>*/}
                                        <td>
                                            <StarRating rating={branch.rating} fontSize={6} />
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end align-items-center flex-nowrap">
                                                <WithRoleAccess permission={CompanyAccess.AccessSettingsView}>
                                                    <OverlayTrigger overlay={<Tooltip>{t("Access settings")}</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={!hasBranchAccess}
                                                            onClick={() => handleAccessSettings(branch.id)}
                                                            className="py-2 text-dark rounded-1"
                                                        >
                                                            <i className="bi bi-person-lock"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger overlay={<Tooltip>{t("Role Management")}</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={!hasBranchAccess}
                                                            onClick={() => handleActiveBranchChange(branch.id)}
                                                            className="py-2 text-dark rounded-1"
                                                        >
                                                            <i className="bi bi-gear"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </WithRoleAccess>
                                                <WithRoleAccess
                                                    anyPermissions={[
                                                        CompanyAccess.BranchView,
                                                        CompanyAccess.BranchEdit,
                                                    ]}
                                                >
                                                    <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={!hasBranchAccess || !canOpenBranchPage}
                                                            onClick={() =>
                                                                navigate(`/edit-branch/${branch.id}/${branchId}`)
                                                            }
                                                            className="p-2 fs-7 text-dark rounded-1"
                                                        >
                                                            <i className="bi bi-pencil px-1"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </WithRoleAccess>
                                                <WithRoleAccess permission={CompanyAccess.BranchDeleteRestore}>
                                                    <OverlayTrigger overlay={<Tooltip>{t("Delete")}</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={
                                                                !hasBranchAccess ||
                                                                !canDeleteRestoreBranch ||
                                                                accessibleActiveBranches.length === 1
                                                            }
                                                            onClick={() => handleOpenConfirmModalBranch(branch.id)}
                                                            className="py-2 text-dark rounded-1"
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </WithRoleAccess>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="w-100 justify-content-md-center">
                    <Col md={10} className="mt-5 pt-5 mx-auto">
                        <h5 className="text-center font-weight-bold">{t("NoDataFoundForTheGivenValues")}</h5>
                    </Col>
                </div>
            )}

            {branches?.length ? (
                <div className="d-flex justify-content-between align-items-center">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : null}
            <BranchModalDelete show={showModalDeleteBranch} handleClose={handleCloseModalBranch} />
            <BranchConfirmDelete
                show={showModalConfirmDeleteBranch && canDeleteRestoreBranch}
                handleClose={handleCloseConfirmModalBranch}
                handleDelete={handleDeleteBranch}
                isLoading={isLoading}
            />
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};

export default Branch;
